import { AskLubavButtonEndpoint } from '@backrs_ui/common-ui/src/models/nugget';
import { HandleAPICallResponseReturn } from '@backrs_ui/common-ui/src/molecules/AskLubavButtons/AskLubavButtonGroup';

type HandleApiCallBaseProps<InputType> = Readonly<{
  url_key: AskLubavButtonEndpoint | undefined;
  apiUrl: string;
  inputData: InputType; // we use a generic type here to allow for different request data types (e.g. CareerPath, Internship, etc.)
  userId?: number;
  getToken?: () => string | null;
  askLubavResponseAccumulator?: (chunk: string) => void;
}>;

/**
 * This function is the base you need for making an unauthorized API call to the Lubav API. It handles the request and response logic.
 *
 * You will need to define what shape is passed in (the Typescript Generic defined here as `InputType`), inside of the UI repo that calls this function.
 *
 * Please see the examples `askLubav.careerPath.util.ts` and `askLubav.internship.util.ts` in Prosper UI for examples of how to use this function.
 */

export async function handleAskLubavApiCall<InputType>({
  url_key,
  apiUrl,
  inputData,
  userId,
  getToken,
  askLubavResponseAccumulator,
}: HandleApiCallBaseProps<InputType>): Promise<HandleAPICallResponseReturn | undefined> {
  const url = `${apiUrl}/api/generate/${url_key}`;
  const accessToken = userId && getToken ? getToken() : undefined;

  try {
    const res = await fetch(url, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
        ...(accessToken ? { Authorization: `Bearer ${accessToken}` } : {}),
      },
      body: JSON.stringify(inputData),
    });

    if (!res.ok) {
      const errorData = await res.json();

      return {
        response: errorData,
        isError: true,
        loadingButton: url_key,
        errorCode: errorData.detail?.code,
      };
    }

    const reader = res.body?.getReader();
    const decoder = new TextDecoder();
    let text = '';

    while (reader) {
      const { done, value } = await reader.read();

      if (done) break;

      const chunk = decoder.decode(value);

      if (askLubavResponseAccumulator) {
        askLubavResponseAccumulator(chunk);
      }

      text += chunk;
    }

    return {
      response: text,
      isError: false,
      loadingButton: url_key,
    };
  } catch (error) {
    console.error({ error });

    return {
      response: undefined,
      isError: true,
      loadingButton: url_key,
    };
  }
}

/**
 * This function attempts to fix JSON that is not properly formatted due to being
 * incomplete. This is needed since we are streaming in a JSON response which, before
 * the stream is complete, will be a partial JSON object.
 *
 * Note this will only work for JSON structures that are of the form:
 * {
 *   "key1": <string> or <array>,
 *   "key2": <string> or <array>,
 *   ...
 * }
 * Nested arrays are not supported.
 */
export const attemptToFixJson = (
  input: string,
  expectedStructure: Record<string, string | string[]>,
): string | undefined => {
  let fixedInput = input.trim();

  // if the input doesn't start with '{' it will not be a valid JSON object
  if (!fixedInput.startsWith('{')) {
    return JSON.stringify(expectedStructure);
  }

  if (!fixedInput.endsWith('}')) {
    // get all keys from the expected structure
    const keys = Object.keys(expectedStructure);

    for (let i = keys.length - 1; i >= 0; i--) {
      const key = keys[i];
      const expectedValue = expectedStructure[key];
      const keyPattern = `"${key}":`;

      if (fixedInput.includes(keyPattern)) {
        const keyStart = fixedInput.lastIndexOf(keyPattern);
        const lastQuote = fixedInput.lastIndexOf('"');
        const lastOpenBracket = fixedInput.lastIndexOf('[');
        const lastCloseBracket = fixedInput.lastIndexOf(']');

        // handle array type
        if (Array.isArray(expectedValue)) {
          if (lastQuote > keyStart && lastQuote > lastOpenBracket) {
            // we are in the middle of a string in an array
            fixedInput += '"]}';
          } else if (lastCloseBracket > lastOpenBracket) {
            // we have complete array items but no closing brackets
            fixedInput += ']}';
          } else if (lastOpenBracket > lastQuote) {
            // we are between array elements
            fixedInput += ']}';
          } else {
            // we just started the array
            fixedInput += ']';
            // add remaining properties
            for (let j = i - 1; j >= 0; j--) {
              fixedInput += `,"${keys[j]}":${JSON.stringify(expectedStructure[keys[j]])}`;
            }

            fixedInput += '}';
          }
        }
        // handle strings
        else {
          fixedInput += '"';
          // add remaining properties
          for (let j = i - 1; j >= 0; j--) {
            fixedInput += `,"${keys[j]}":${JSON.stringify(expectedStructure[keys[j]])}`;
          }

          fixedInput += '}';
        }

        break;
      }
    }
  }

  try {
    const parsed = JSON.parse(fixedInput);

    // ensure all expected properties exist with their default values
    return JSON.stringify({
      ...expectedStructure,
      ...parsed,
    });
  } catch {
    // returned undefined to indicate we should use the last valid JSON object
    return undefined;
  }
};
